import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ContactForm from './components/ContactForm';
import ContactInfo from './components/ContactInfo';
import SuccessModal from './components/SuccessModal';
import ContactHub from '../../components/ui/ContactHub';
import ShoppingCart from '../../components/ui/ShoppingCart';
import Icon from '../../components/AppIcon';

const SmartContact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submissionData, setSubmissionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate page load
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleFormSubmit = async (formData) => {
    setIsSubmitting(true);
    
    try {
      // Simulate API submission
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      console.log('Form submitted:', formData);
      
      // Store submission data for success modal
      setSubmissionData(formData);
      setShowSuccessModal(true);
      
      // Track submission analytics
      if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
        window.gtag('event', 'form_submit', {
          event_category: 'Contact',
          event_label: formData?.inquiryType,
          value: 1
        });
      }
      
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setSubmissionData(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Icon name="MessageCircle" size={24} color="white" />
          </div>
          <p className="text-text-secondary">Loading contact form...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Contact Us - Smart Contact Form | Clean360</title>
        <meta 
          name="description" 
          content="Get in touch with Clean360 through our intelligent contact form. AI-powered routing, instant quotes, and immediate response for all your cleaning service needs." 
        />
        <meta name="keywords" content="contact clean360, cleaning service contact, get quote, emergency cleaning, professional cleaning contact" />
        <meta property="og:title" content="Contact Clean360 - Professional Cleaning Services" />
        <meta property="og:description" content="Smart contact form with AI-powered routing and instant response. Get quotes, schedule services, or request emergency cleaning assistance." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://clean360.com/smart-contact" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-20 pb-12 bg-gradient-to-br from-primary/5 to-accent/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                  <Icon name="MessageCircle" size={24} color="white" />
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold text-text-primary">
                  Smart Contact
                </h1>
              </div>
              <p className="text-xl text-text-secondary mb-8">
                Get in touch with our AI-enhanced contact system. Intelligent routing, 
                instant quotes, and immediate response for all your cleaning needs.
              </p>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-2xl mx-auto">
                <div className="text-center">
                  <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon name="Clock" size={24} color="var(--color-success)" />
                  </div>
                  <h3 className="font-semibold text-text-primary">15 Min Response</h3>
                  <p className="text-sm text-text-secondary">For emergency requests</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon name="Brain" size={24} color="var(--color-primary)" />
                  </div>
                  <h3 className="font-semibold text-text-primary">AI-Powered</h3>
                  <p className="text-sm text-text-secondary">Smart form routing</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon name="Shield" size={24} color="var(--color-accent)" />
                  </div>
                  <h3 className="font-semibold text-text-primary">Secure & Private</h3>
                  <p className="text-sm text-text-secondary">Your data protected</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Contact Form - Takes up 2 columns on large screens */}
              <div className="lg:col-span-2">
                <ContactForm 
                  onSubmit={handleFormSubmit}
                  isSubmitting={isSubmitting}
                />
              </div>

              {/* Contact Information - Takes up 1 column on large screens */}
              <div className="lg:col-span-1">
                <ContactInfo />
              </div>
            </div>
          </div>
        </section>

        {/* Additional Features Section */}
        <section className="py-12 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-text-primary mb-4">
                Why Use Our Smart Contact System?
              </h2>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                Experience the future of customer communication with our AI-enhanced contact platform
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow-elevation-1 p-6 text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Zap" size={24} color="var(--color-primary)" />
                </div>
                <h3 className="font-semibold text-text-primary mb-2">Instant Routing</h3>
                <p className="text-sm text-text-secondary">
                  AI automatically routes your inquiry to the right department for faster response
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-elevation-1 p-6 text-center">
                <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="MapPin" size={24} color="var(--color-accent)" />
                </div>
                <h3 className="font-semibold text-text-primary mb-2">Smart Autocomplete</h3>
                <p className="text-sm text-text-secondary">
                  Google Places integration automatically fills in your address details
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-elevation-1 p-6 text-center">
                <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Upload" size={24} color="var(--color-success)" />
                </div>
                <h3 className="font-semibold text-text-primary mb-2">File Upload</h3>
                <p className="text-sm text-text-secondary">
                  Drag and drop photos for accurate project estimates and better service
                </p>
              </div>

              <div className="bg-white rounded-lg shadow-elevation-1 p-6 text-center">
                <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="MessageSquare" size={24} color="var(--color-warning)" />
                </div>
                <h3 className="font-semibold text-text-primary mb-2">WhatsApp Handoff</h3>
                <p className="text-sm text-text-secondary">
                  Seamlessly continue conversations on WhatsApp for instant communication
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Signals */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-lg shadow-elevation-2 p-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-text-primary mb-4">
                  Trusted by Thousands of Customers
                </h2>
                <p className="text-text-secondary">
                  Join our satisfied customers who trust Clean360 for all their cleaning needs
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-primary mb-2">500+</div>
                  <div className="text-sm text-text-secondary">Happy Customers</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-success mb-2">4.9★</div>
                  <div className="text-sm text-text-secondary">Average Rating</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-accent mb-2">24/7</div>
                  <div className="text-sm text-text-secondary">Emergency Service</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-warning mb-2">$2M</div>
                  <div className="text-sm text-text-secondary">Insurance Coverage</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Success Modal */}
        <SuccessModal
          isOpen={showSuccessModal}
          onClose={handleSuccessModalClose}
          submissionData={submissionData}
        />

        {/* Floating Components */}
        <ContactHub />
        <ShoppingCart />
      </div>
    </>
  );
};

export default SmartContact;